import { baseApi } from '@core/api/baseApi';
import type {
  User,
  CreateUserRequest,
  UpdateUserRequest,
  SearchUsersRequest,
  PaginatedResult,
  RolesResponse,
  RoleManagementResponse,
  CreateRoleRequest,
  UpdateRolePermissionsRequest,
  RolePermissionsResponse,
  UserAccessResponse,
  UpdateUserAccessRequest,
  EmployeeAccountCandidate,
  EmployeeAccountStatus,
} from '../types/user.types';

export interface UserDetailsResponse {
  userId: string;
  roles: string[];
  permissions: string[];
}

export interface UserProfileResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  permissions: string[];
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * Users module API
 *
 * Provides user management endpoints:
 * - Current user's roles+permissions
 * - User CRUD operations
 * - User search with pagination
 * - Roles list
 */
export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get current user's roles and permissions
     * Backend endpoint: GET /api/users/roles/userDetails
     */
    getUserDetails: builder.query<UserDetailsResponse, void>({
      query: () => ({
        url: '/users/roles/userDetails',
        method: 'GET',
      }),
    }),

    /**
     * Search users with pagination and filters
     * Backend endpoint: POST /api/users/users/search
     */
    searchUsers: builder.mutation<PaginatedResult<User>, SearchUsersRequest>({
      query: (params) => ({
        url: '/users/users/search',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    /**
     * Create a new user
     * Backend endpoint: POST /api/users/users
     * Returns: Guid (user ID)
     */
    createUser: builder.mutation<string, CreateUserRequest>({
      query: (user) => ({
        url: '/users/users',
        method: 'POST',
        body: user,
      }),
      invalidatesTags: [
        { type: 'User', id: 'LIST' },
        { type: 'EmployeeAccountCandidate', id: 'LIST' },
        { type: 'EmployeeAccountStatus', id: 'LIST' },
      ],
    }),

    getEmployeeAccountCandidates: builder.query<EmployeeAccountCandidate[], void>({
      query: () => ({
        url: '/users/users/employee-account-candidates',
        method: 'GET',
      }),
      providesTags: [{ type: 'EmployeeAccountCandidate', id: 'LIST' }],
    }),

    getEmployeeAccountStatus: builder.query<EmployeeAccountStatus[], void>({
      query: () => ({
        url: '/users/users/employee-account-status',
        method: 'GET',
      }),
      providesTags: [{ type: 'EmployeeAccountStatus', id: 'LIST' }],
    }),

    /**
     * Update an existing user
     * Backend endpoint: PUT /api/users/users
     */
    updateUser: builder.mutation<void, UpdateUserRequest>({
      query: (user) => ({
        url: '/users/users',
        method: 'PUT',
        body: user,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    /**
     * Archive a user
     * Backend endpoint: PUT /api/users/users/archive  { userId }
     */
    archiveUser: builder.mutation<void, string>({
      query: (id) => ({
        url: '/users/users/archive',
        method: 'PUT',
        body: { userId: id },
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    /**
     * Restore an archived user
     * Backend endpoint: PUT /api/users/users/restore  { userId }
     */
    restoreUser: builder.mutation<void, string>({
      query: (id) => ({
        url: '/users/users/restore',
        method: 'PUT',
        body: { userId: id },
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'User', id },
        { type: 'User', id: 'LIST' },
      ],
    }),

    /**
     * Get list of all available roles
     * Backend endpoint: GET /api/users/roles/list
     */
    getRoles: builder.query<RolesResponse, void>({
      query: () => ({
        url: '/users/roles/list',
        method: 'GET',
      }),
      providesTags: [{ type: 'Role', id: 'LIST' }],
    }),

    getRoleSummaries: builder.query<RoleManagementResponse, void>({
      query: () => ({
        url: '/users/roles',
        method: 'GET',
      }),
      providesTags: [{ type: 'Role', id: 'LIST' }],
    }),

    createRole: builder.mutation<string, CreateRoleRequest>({
      query: (body) => ({
        url: '/users/roles',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Role', id: 'LIST' }],
    }),

    getRolePermissions: builder.query<RolePermissionsResponse, void>({
      query: () => ({
        url: '/users/roles/permissions',
        method: 'GET',
      }),
      providesTags: [
        { type: 'Role', id: 'PERMISSIONS' },
        { type: 'Permission', id: 'LIST' },
      ],
    }),

    updateRolePermissions: builder.mutation<void, UpdateRolePermissionsRequest>({
      query: ({ roleName, permissions }) => ({
        url: `/users/roles/${encodeURIComponent(roleName)}/permissions`,
        method: 'PUT',
        body: { permissions },
      }),
      invalidatesTags: (_result, _error, { roleName }) => [
        { type: 'Role', id: 'LIST' },
        { type: 'Role', id: 'PERMISSIONS' },
        { type: 'Role', id: roleName },
      ],
    }),

    /**
     * Get current user's profile
     * Backend endpoint: GET /api/users/users
     */
    getCurrentUserProfile: builder.query<UserProfileResponse, void>({
      query: () => ({
        url: '/users/users',
        method: 'GET',
      }),
      providesTags: [{ type: 'User', id: 'CURRENT' }],
    }),

    /**
     * Get a user by ID
     * Backend endpoint: GET /api/users/users/{id}
     */
    getUserById: builder.query<User, string>({
      query: (id) => ({
        url: `/users/users/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'User', id }],
    }),

    getUserAccess: builder.query<UserAccessResponse, string>({
      query: (id) => ({
        url: `/users/users/${id}/access`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'User', id: `${id}-access` }],
    }),

    updateUserAccess: builder.mutation<void, UpdateUserAccessRequest>({
      query: ({ userId, roles, directPermissions }) => ({
        url: `/users/users/${userId}/access`,
        method: 'PUT',
        body: { roles, directPermissions },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'User', id: userId },
        { type: 'User', id: `${userId}-access` },
        { type: 'User', id: 'LIST' },
        { type: 'Role', id: 'LIST' },
      ],
    }),

    /**
     * Change current user's password
     * Backend endpoint: POST /api/users/users/change-password
     */
    changePassword: builder.mutation<void, ChangePasswordRequest>({
      query: (data) => ({
        url: '/users/users/change-password',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetUserDetailsQuery,
  useSearchUsersMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useArchiveUserMutation,
  useRestoreUserMutation,
  useGetRolesQuery,
  useGetRoleSummariesQuery,
  useCreateRoleMutation,
  useGetRolePermissionsQuery,
  useUpdateRolePermissionsMutation,
  useGetEmployeeAccountCandidatesQuery,
  useGetEmployeeAccountStatusQuery,
  useGetCurrentUserProfileQuery,
  useGetUserByIdQuery,
  useGetUserAccessQuery,
  useUpdateUserAccessMutation,
  useChangePasswordMutation,
} = usersApi;

